/* BattleHUD — top-of-screen player+opponent panels and the small
 * center-stage HUD (round tracker, momentum meter).
 * Small, focused sub-components live in this same file so the arena
 * page can just import <BattleHUD /> and go.
 */
import { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Shield, Skull, Zap, Flame } from 'lucide-react';

// -----------------------------------------------------------------------
// HPBar — smoothly transitions between values. States:
//   100–51%  → normal (emerald)
//   50–26%   → warning (amber)
//   25–1%    → critical (rose, pulses)
//   0%       → defeated
// -----------------------------------------------------------------------
export const HPBar = memo(function HPBar({ current, max, side = 'challenger', small = false }) {
  const pct = Math.max(0, Math.min(100, max > 0 ? (current / max) * 100 : 0));
  const state = pct === 0 ? 'defeated' : pct <= 25 ? 'critical' : pct <= 50 ? 'warning' : 'normal';
  const color = {
    normal: 'from-emerald-400 to-emerald-600',
    warning: 'from-amber-300 to-amber-500',
    critical: 'from-rose-400 to-red-600',
    defeated: 'from-neutral-700 to-neutral-900',
  }[state];
  const pulse = state === 'critical';
  return (
    <div className="w-full" data-testid={`hp-bar-${side}`}>
      <div className={`h-2.5 bg-black/60 rounded-full overflow-hidden border border-white/10 ${pulse ? 'animate-pulse' : ''}`}>
        <motion.div
          className={`h-full bg-gradient-to-r ${color}`}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        />
      </div>
      {!small && (
        <div className="flex items-baseline justify-between mt-1 text-[9px] font-mono tabular-nums">
          <span className={state === 'critical' ? 'text-rose-300' : 'text-white/70'}>
            {Math.max(0, Math.round(current))}
          </span>
          <span className="text-white/40">/ {Math.round(max)}</span>
        </div>
      )}
    </div>
  );
});

// -----------------------------------------------------------------------
// StatusEffects — chips with tooltip. Server sends {code, name} pairs.
// -----------------------------------------------------------------------
const STATUS_META = {
  poison:          { color: '#84cc16', icon: Skull, desc: 'Losing HP each round.' },
  bleeding:        { color: '#ef4444', icon: Flame, desc: 'Small chip damage each round.' },
  frozen:          { color: '#38bdf8', icon: Shield, desc: 'Turn skipped this round.' },
  stunned:         { color: '#a78bfa', icon: Zap,    desc: 'Cannot act next round.' },
  fear:            { color: '#f472b6', icon: Skull, desc: 'Reduced accuracy while shaken.' },
  fearful:         { color: '#f472b6', icon: Skull, desc: 'Reduced accuracy while shaken.' },
  confusion:       { color: '#f59e0b', icon: Zap,    desc: 'May target itself.' },
  blind:           { color: '#94a3b8', icon: Zap,    desc: 'Miss chance up.' },
  rooted:          { color: '#22c55e', icon: Shield, desc: 'Cannot flee — bonus defense.' },
  regeneration:    { color: '#4ade80', icon: Sparkles, desc: 'Regenerating HP.' },
  critical_focus:  { color: '#e9c46a', icon: Sparkles, desc: 'Crit chance up.' },
  fatigue:         { color: '#94a3b8', icon: Shield, desc: 'Reduced attack.' },
};

export function StatusEffects({ list = [], side = 'challenger' }) {
  if (!list?.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${side === 'opponent' ? 'justify-end' : 'justify-start'}`}
         data-testid={`status-effects-${side}`}>
      {list.map((s, i) => {
        const meta = STATUS_META[s.code] || { color: '#94a3b8', icon: Sparkles, desc: s.name || s.code };
        const Icon = meta.icon;
        return (
          <span
            key={`${s.code}-${i}`}
            title={`${s.name || s.code} — ${meta.desc}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest border"
            style={{
              backgroundColor: meta.color + '22',
              borderColor: meta.color + '55',
              color: meta.color,
            }}
          >
            <Icon className="w-2.5 h-2.5" strokeWidth={2.5} />
            {s.name || s.code}
          </span>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------
// PlayerPanel — nameplate + HP bar + status chips
// -----------------------------------------------------------------------
export const PlayerPanel = memo(function PlayerPanel({ side, name, avatar, snapshot, hp, hpMax, status, isYou }) {
  const align = side === 'opponent' ? 'items-end text-right' : 'items-start text-left';
  return (
    <div className={`flex flex-col gap-1 ${align} min-w-0 flex-1`} data-testid={`player-panel-${side}`}>
      <div className={`flex ${side === 'opponent' ? 'flex-row-reverse' : 'flex-row'} items-center gap-2 min-w-0`}>
        <img
          src={avatar}
          alt=""
          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          className="w-8 h-8 rounded-full object-cover border border-white/25 shrink-0"
        />
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-widest text-white/60 truncate">
            {isYou ? 'You' : (side === 'opponent' ? 'Opponent' : 'Challenger')}
          </p>
          <p className="font-serif text-sm leading-tight truncate">{name}</p>
        </div>
      </div>
      <HPBar current={hp} max={hpMax} side={side} />
      <StatusEffects list={status} side={side} />
    </div>
  );
});

// -----------------------------------------------------------------------
// RoundTracker — dots showing best-of series score.
// -----------------------------------------------------------------------
export function RoundTracker({ bestOf = 5, wins = { challenger: 0, opponent: 0 }, isFinal = false }) {
  const needed = Math.floor(bestOf / 2) + 1;
  const row = (side, filled) => Array.from({ length: needed }).map((_, i) => (
    <span
      key={`${side}-${i}`}
      className={`w-2 h-2 rounded-full transition-all ${
        i < filled
          ? (side === 'challenger' ? 'bg-emerald-400 shadow-[0_0_6px_2px_rgba(52,211,153,0.6)]' : 'bg-rose-400 shadow-[0_0_6px_2px_rgba(251,113,133,0.6)]')
          : 'bg-white/15'
      }`}
    />
  ));
  return (
    <div className="flex flex-col items-center gap-1" data-testid="round-tracker">
      {isFinal && (
        <motion.p
          initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="text-[9px] uppercase tracking-[0.3em] text-amber-300 font-bold animate-pulse"
        >
          Final Round
        </motion.p>
      )}
      <div className="flex items-center gap-2">
        <div className="flex gap-1">{row('challenger', wins.challenger || 0)}</div>
        <span className="text-white/30 text-[10px] font-mono">Bo{bestOf}</span>
        <div className="flex gap-1">{row('opponent', wins.opponent || 0)}</div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// MomentumMeter — subtle horizontal bar sliding toward the side gaining
// control. Value: -1 (fully opponent) to +1 (fully challenger).
// -----------------------------------------------------------------------
export function MomentumMeter({ value = 0 }) {
  const pct = Math.max(-1, Math.min(1, value)) * 50 + 50; // 0..100
  return (
    <div className="w-full max-w-[220px] mx-auto space-y-0.5" data-testid="momentum-meter">
      <div className="flex items-center justify-between text-[8px] uppercase tracking-widest text-white/40">
        <span>Challenger</span>
        <span>Momentum</span>
        <span>Opponent</span>
      </div>
      <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="absolute top-0 h-full bg-gradient-to-r from-emerald-400 to-rose-400 rounded-full"
          animate={{ left: `${Math.min(pct, 100 - 4)}%`, width: '4%' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// RoundBanner — big "ROUND N" flash overlay between rounds.
// -----------------------------------------------------------------------
export function RoundBanner({ round, isFinal, subtitle }) {
  return (
    <AnimatePresence>
      <motion.div
        key={`round-${round}`}
        initial={{ opacity: 0, y: -20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4 }}
        className="pointer-events-none absolute inset-x-0 top-[38%] flex flex-col items-center z-30"
        data-testid={`round-banner-${round}`}
      >
        <p className={`text-[10px] uppercase tracking-[0.4em] ${isFinal ? 'text-amber-300' : 'text-white/60'}`}>
          {isFinal ? 'Final Round' : `Round ${round}`}
        </p>
        {isFinal && (
          <p className="font-serif text-4xl bg-gradient-to-b from-amber-200 to-amber-400 bg-clip-text text-transparent drop-shadow">
            {String(round).padStart(2, '0')}
          </p>
        )}
        {subtitle && <p className="text-xs text-white/70 mt-1">{subtitle}</p>}
      </motion.div>
    </AnimatePresence>
  );
}
